import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import IntervalTree, { Interval } from '@flatten-js/interval-tree';
import { EventApi } from '@fullcalendar/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select, Store } from '@ngxs/store';
import {
  ConfigurationService,
  TimezoneService,
} from '@sc/data-access-configuration';
import {
  DossierAgendaFiltersInput,
  DossierInput,
} from '@sc/data-access-graphql';
import {
  AgendaHttpService,
  CarHttpService,
  DossierHttpService,
  EventSubHttpService,
  FittersHttpService,
  LocationHttpService,
  StorageHttpService,
} from '@sc/data-access-http-services';
import {
  AgendaEvent,
  Appointment,
  CancelAppointmentApi,
  Car,
  Dossier,
  DossierCommentsEnum,
  DossierComments,
  Fitter,
  FitterAccessType,
  FitterResource,
  FitterServiceType,
  FitterVehicle,
  Glass,
  LicensePlateNumberMask,
  LocationData,
  LocationDataGeocoded,
  LoggerLevelType,
  NotifyAppointmentApi,
  getLatestDossierCommentTextByType,
} from '@sc/data-access-models';
import { PatchClaimAction } from '@sc/data-access-state-management';
import {
  JsonListKey,
  JsonObjectKey,
  StorageService,
} from '@sc/data-access-storage';
import { TranslatorService } from '@sc/data-access-translation';
import { AuthService } from '@sc/feature-auth-okta';
import { FitterNameDisplayService } from '@sc/feature-contextual-display';
import {
  DossierEventExtendedProps,
  DossierManagerService,
  EventExtendedProps,
  FittersManagerService,
  GenericEventExtendedProps,
  Resource,
  ResourceType,
  ResourceTypeEnum,
  ResourcesManagerService,
  SetResourcesAction,
} from '@sc/feature-fitter-shared-libs';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { LoadingService } from '@sc/feature-loader';
import { SnackbarService } from '@sc/feature-snackbar';
import { Task } from '@sc/ui-components';
import { dayjs, getPeriodReplacementVehicleUse } from '@sc/utils';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest,
  forkJoin,
  noop,
  of,
  throwError,
} from 'rxjs';
import { filter, map, switchMap, switchMapTo, take, tap } from 'rxjs/operators';
import { CalendarViewParams } from '../models';
import {
  AddAgendaEventsAction,
  ReplaceAgendaEventAction,
  ReplaceAgendaEventsAction,
  ReplaceDossiersRequestedAppointmentAction,
  ReplaceDossiersToValidateAction,
  SetAgendaEventsAction,
  SetDossiersRequestedAppointmentAction,
  SetDossiersToValidateAction,
  SetNewDossierRequestedAppointmentAction,
  SetNewDossiersToValidateAction,
} from '../store';
import { PlanningCalendarService } from './planning-calendar.service';

@Injectable()
export class PlanningSandboxService implements OnDestroy {
  static readonly allTechniciansFilterKey = 'allTechnicians';
  static readonly notSetTechniciansFilterKey = 'notSetTechnician';
  static readonly allInterventionVehiclesFilterKey = 'allInterventionVehicles';
  static readonly noSetInterventionVehiclesFilterKey =
    'noSetInterventionVehicle';

  @Select((state) => state.agendaEvents)
  events$: Observable<EventApi[]>;

  @Select((state) => state.dossiersToValidate)
  externalDossiersToValidate$: Observable<Dossier[]>;

  @Select((state) => state.dossiersRequestedAppointment)
  externalAppointmentRequests$: Observable<Dossier[]>;

  @Select((state) => state.agendaResources.fitters)
  fitters$: Observable<Fitter[]>;

  @Select((state) => state.agendaResources.resources)
  resources$: Observable<Resource[]>;

  isLoadingEvents$ = new BehaviorSubject<boolean>(false);
  isLoadingFitters$ = new BehaviorSubject<boolean>(false);
  rerenderPlanning$ = new BehaviorSubject<boolean>(false);
  externalChangesOnDossier$ = new BehaviorSubject<Dossier>(null);
  currentTimeFrameStart = dayjs().add(-7, 'day').format('YYYY-MM-DD');
  currentTimeFrameEnd = dayjs().add(13, 'day').format('YYYY-MM-DD');

  shownExternalDossiersToValidate$: Observable<Dossier[]>;
  shownExternalAppointmentRequests$: Observable<Dossier[]>;

  autoCompleteCarSelection$ = new BehaviorSubject<Car>(null);

  calendarEventsTree = new IntervalTree<EventApi>();

  subs = new Subscription();

  constructor(
    private readonly planningCalendarService: PlanningCalendarService,
    private readonly agendaHttpService: AgendaHttpService,
    private readonly fittersHttpService: FittersHttpService,
    private readonly eventSubHttpService: EventSubHttpService,
    private readonly carHttpService: CarHttpService,
    private readonly configurationService: ConfigurationService,
    private readonly locationHttpService: LocationHttpService,
    private readonly authOktaService: AuthService,
    private readonly storageHttpService: StorageHttpService,
    private readonly dossierHttpService: DossierHttpService,
    private readonly dossierManagerService: DossierManagerService,
    private readonly loadingService: LoadingService,
    private readonly fittersManagerService: FittersManagerService,
    private readonly resManagerService: ResourcesManagerService,
    private readonly store: Store,
    private readonly translator: TranslatorService,
    private readonly storage: StorageService,
    private readonly snackbarService: SnackbarService,
    private readonly timezoneService: TimezoneService,
    private readonly fitterNameDisplayService: FitterNameDisplayService,
    private readonly featureFlippingService: FeatureFlippingManagerService
  ) {
    this.shownExternalDossiersToValidate$ =
      this.externalDossiersToValidate$.pipe(
        switchMap((dossiers) =>
          this.resources$.pipe(
            map(
              (resources) =>
                dossiers?.filter(
                  (dossier) =>
                    resources.findIndex(
                      (res) =>
                        res.fitterId === dossier.appointment?.fitterId &&
                        res.display
                    ) > -1
                ) || []
            )
          )
        ),
        map((dossiers) =>
          dossiers.filter((d, i) => {
            // Remove duplicates
            for (let j = 0; j < i; j++) {
              if (dossiers[j].id === d.id) {
                return false;
              }
            }

            return true;
          })
        )
      );

    this.shownExternalAppointmentRequests$ =
      this.externalAppointmentRequests$.pipe(
        switchMap((dossiers) =>
          this.resources$.pipe(
            map(
              (resources) =>
                dossiers?.filter(
                  (dossier) =>
                    resources.findIndex(
                      (res) =>
                        res.fitterId === dossier.requestAppointment?.fitterId &&
                        res.display
                    ) > -1
                ) || []
            )
          )
        ),
        map((dossiers) =>
          dossiers.filter((d, i) => {
            // Remove duplicates
            for (let j = 0; j < i; j++) {
              if (dossiers[j].id === d.id) {
                return false;
              }
            }

            return true;
          })
        )
      );

    const subNewDossier = this.eventSubHttpService
      .subscribeToNewDossier()
      .subscribe((newDossier) => {
        if (newDossier) {
          if (
            newDossier.dossier?.requestAppointment &&
            !newDossier.dossier?.appointment
          ) {
            // If the dossier is an appointment request, then save it as such
            this.storeNewDossierRequestedApmt(newDossier.dossier);
          } else if (
            newDossier.dossier.creationData.email !==
              this.authOktaService.getAccountLogin() &&
            !newDossier.dossier?.requestAppointment
          ) {
            // Do not update front-end if the dossier comes from the same user (update is likely already done) or came from requested appointment
            // Add the dossier either directly on the calendar if validated or in the validation basket otherwise
            if (newDossier.dossier.appointment?.isValidatedByFitter) {
              this.externalChangesOnDossier$.next(newDossier.dossier);

              // If the dossier has previous appointments, this means that it was booked after a cancel/no-show and we should not update it on calendar
              if (!newDossier.dossier.previousAppointments?.length) {
                this.fitters$.pipe(take(1)).subscribe((fitters) => {
                  const newDossiersEvents =
                    this.planningCalendarService.constructAllCalendarDossierEvents(
                      newDossier.dossier,
                      fitters
                    );

                  this.addAgendaEvents(newDossiersEvents).subscribe();
                });
              }
            } else {
              this.storeNewDossiersToValidate([newDossier.dossier]);
            }
          }
        }
      });

    const subUpdateDossier = this.eventSubHttpService
      .subscribeToUpdatedDossier()
      .subscribe((updatedDossier) => {
        if (updatedDossier) {
          if (
            updatedDossier.dossier.creationData.email !==
              this.authOktaService.getAccountLogin() &&
            !updatedDossier.dossier?.requestAppointment
          ) {
            this.externalChangesOnDossier$.next(updatedDossier.dossier);
          }

          this.fitters$
            .pipe(
              take(1),
              switchMap((fitters) =>
                this.updateEventsFromDossier(updatedDossier.dossier, fitters)
              )
            )
            .subscribe();
        }
      });

    const subDepartingDossier = this.eventSubHttpService
      .subscribeToDepartingDossier()
      .pipe(
        filter((departingDossier) => {
          const knownFitterIds = this.fittersManagerService.getSavedFitterIds();

          return (
            knownFitterIds.includes(departingDossier.oldFitterId) &&
            !knownFitterIds.includes(departingDossier.newFitterId)
          );
        }),
        // Every dossier arriving here must be removed from all known dossier list
        switchMap((departingDossier) =>
          forkJoin([
            this.replaceAgendaEvent(departingDossier.dossierId, []),
            this.removeDossiersToValidate([departingDossier.dossierId]),
            this.removeDossiersRequestApmt([departingDossier.dossierId]),
          ])
        )
      )
      .subscribe();

    const subEventsTree = this.events$.subscribe((events) => {
      this.calendarEventsTree.clear();
      events.forEach((event) => {
        const eventInterval = new Interval(
          dayjs(event.start).utc().format(),
          dayjs(event.end).utc().format()
        );
        this.calendarEventsTree.insert(eventInterval, event);
      });
    });

    this.subs.add(subNewDossier);
    this.subs.add(subUpdateDossier);
    this.subs.add(subDepartingDossier);
    this.subs.add(subEventsTree);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  getAllEventsIntersectingDossier(dossier: Dossier) {
    try {
      const apmtTime = dayjs(dossier.appointment?.time);
      const apmtTimeEnd = dayjs(dossier.appointment?.endTime);

      if (!apmtTime.isValid() || !apmtTimeEnd.isValid()) {
        return [];
      }

      const rvUse = getPeriodReplacementVehicleUse(dossier);
      const rvUseStart = dayjs(rvUse.start);
      const rvUseEnd = dayjs(rvUse.end);
      const earliestDossierTime = apmtTime.isSameOrBefore(rvUseStart)
        ? apmtTime
        : rvUseStart;
      const latestDossierTime = apmtTimeEnd.isSameOrAfter(rvUseEnd)
        ? apmtTimeEnd
        : rvUseEnd;

      if (!earliestDossierTime.isValid() || !latestDossierTime.isValid()) {
        return [];
      }

      const dossierInterval = new Interval(
        earliestDossierTime.utc(true).format(),
        latestDossierTime.utc(true).format()
      );

      const agendaEvents = this.calendarEventsTree
        .search(dossierInterval)
        .slice();

      return agendaEvents;
    } catch (err) {
      console.warn(err);
      return [];
    }
  }

  askToRerenderPlanning() {
    this.rerenderPlanning$.next(true);
  }

  initPlanning() {
    this.fetchFitters()
      .pipe(
        switchMap((fitters) => {
          return combineLatest([of(fitters), this.getResources(fitters)]);
        })
      )
      .pipe(
        switchMap(([fitters, resources]) =>
          this.getEvents(
            fitters,
            resources,
            this.currentTimeFrameStart,
            this.currentTimeFrameEnd
          )
        )
      )
      .subscribe(noop, (error) => {
        this.snackbarService.showError(
          this.translator.getInstantTranslation('Snackbar.errorTitle'),
          this.translator.getInstantTranslation(
            error?.error?.message || 'Planning.initPlanningError'
          ),
          5,
          LoggerLevelType.WARNING
        );

        console.error(error);
      });
  }

  fetchFitters() {
    return this.fittersManagerService.getOrFetchCurrentFitters().pipe(
      map((fitters) =>
        combineLatest([
          fitters.filter((f) =>
            this.authOktaService
              .userIsManagerWithGroup$(
                [f],
                `grpFitter_${
                  this.configurationService.getConfig().id
                }` as FitterAccessType,
                true
              )
              .pipe(
                filter((isManager) => isManager) // Filter managers dynamically
              )
          ),
        ]).pipe(map(() => fitters.filter((f) => true)))
      ),
      switchMap((fitters) => {
        return fitters;
      })
    );
  }

  updatePlanning(
    shouldGetEvents = true,
    allowEmpty = false,
    fitterIds?: string[],
    begin?: string,
    end?: string
  ) {
    const sameFrame = begin === this.currentTimeFrameStart;
    if (!begin) {
      begin = this.currentTimeFrameStart;
      end = this.currentTimeFrameEnd;
    } else {
      this.currentTimeFrameStart = begin;
      this.currentTimeFrameEnd = end;
    }
    const usedFitterIds =
      fitterIds || this.fittersManagerService.getSavedFitterIds();
    const currentFitterIds: string[] = [];
    this.fitters$
      .pipe(
        take(1),
        tap((fitters) =>
          currentFitterIds.push(...(fitters?.map((f) => f.id) || []))
        ),
        switchMapTo(
          this.fittersManagerService.setPlanningFitters(
            usedFitterIds,
            allowEmpty
          )
        ),
        map((fitters) =>
          combineLatest([
            fitters?.filter((f) =>
              this.authOktaService
                .userIsManagerWithGroup$(
                  [f],
                  `grpFitter_${
                    this.configurationService.getConfig().id
                  }` as FitterAccessType,
                  true
                )
                .pipe(
                  filter((isManager) => isManager) // Filter managers dynamically
                )
            ),
          ]).pipe(map(() => fitters.filter((f) => true) || []))
        ),
        switchMap((fitters) => {
          return fitters;
        }),
        switchMap((fitters) => {
          return combineLatest([of(fitters), this.getResources(fitters)]);
        }),
        switchMap(([fitters, resources]) => {
          const allSameFitters = fitters.reduce(
            (allSameAcc, currentF) =>
              allSameAcc && currentFitterIds.includes(currentF.id),
            true
          );

          if (!allSameFitters || shouldGetEvents || !sameFrame) {
            return this.getEvents(fitters, resources, begin, end);
          } else {
            return of(null);
          }
        })
      )
      .subscribe(
        () => {
          this.askToRerenderPlanning();
          this.loadingService.setLoading(false);
        },
        (error) => {
          this.loadingService.setLoading(false);
          this.snackbarService.showError(
            this.translator.getInstantTranslation('Snackbar.errorTitle'),
            error?.error?.message ||
              this.translator.getInstantTranslation(
                'Planning.updatePlanningError'
              ),
            5,
            LoggerLevelType.WARNING
          );

          console.error(error);
        }
      );
  }

  updatePlanningDossierChange(updatedDossier: Dossier) {
    this.fitters$
      .pipe(
        take(1),
        switchMap((fitters) =>
          this.updateEventsFromDossier(updatedDossier, fitters)
        )
      )
      .subscribe(
        () => this.loadingService.setLoading(false),
        (error) => {
          this.loadingService.setLoading(false);
          this.snackbarService.showError(
            this.translator.getInstantTranslation('Snackbar.errorTitle'),
            error?.error?.message ||
              this.translator.getInstantTranslation(
                'Planning.updatePlanningError'
              ),
            5,
            LoggerLevelType.WARNING
          );

          console.error(error);
        }
      );
  }

  getDossier(dossierId: string) {
    const currentDossier = this.getAgendaEventsSnapshot()?.find((event) => {
      const extendedProps = event?.extendedProps as EventExtendedProps;
      const isDossier =
        this.resManagerService.isDossierEventExtendedProps(extendedProps);

      return isDossier && extendedProps.dossier?.id === dossierId;
    });

    if (currentDossier) {
      return of(currentDossier?.extendedProps?.dossier);
    } else {
      return this.dossierHttpService.fetchDossier({ id: dossierId });
    }
  }

  public setIsAuth(isauth: boolean) {
    this.eventSubHttpService.setIsAuth(isauth);
  }

  storeResourcesFilters(filters: Task[]) {
    this.storage.setItem('agenda-resource-filters', filters, localStorage, []);
  }

  getStoredResourcesFilters() {
    return (
      this.storage.getItem<JsonListKey, Task>(
        'agenda-resource-filters',
        localStorage,
        []
      ) || []
    );
  }

  generateFitterSubfilter(
    fitter: Fitter,
    resType: ResourceType,
    previousFitterFilter: Task,
    options?: {
      techName?: string;
      interventionVehicleName?: string;
      rvName?: string;
      taskName?: string;
      techSubtasks?: boolean;
      interventionVehicleSubtasks?: boolean;
    }
  ): Task {
    let subtaskId = this.resManagerService.constructResourceId(
      fitter.id,
      resType,
      resType === 'TECHNICIAN'
        ? options?.techName
        : resType === 'INTERVENTION_VEHICLE'
        ? options?.interventionVehicleName
        : resType === 'RV_DETAILED'
        ? options?.rvName
        : undefined
    );

    // Use different id construction if technician is not attributed
    if (resType === 'TECHNICIAN' && !options?.techName) {
      subtaskId = this.resManagerService.constructDefaultTechResourceId(
        fitter.id
      );
    }
    // Use different id construction if interventionVehicle is not attributed
    if (
      resType === 'INTERVENTION_VEHICLE' &&
      !options?.interventionVehicleName
    ) {
      subtaskId = this.resManagerService.constructDefaultInterventionVehicleId(
        fitter.id
      );
    }
    // Use different id construction if RV is not attributed
    if (resType === 'RV_DETAILED' && !options?.rvName) {
      subtaskId = this.resManagerService.constructDefaultRVResourceId(
        fitter.id
      );
    }

    const previousSubtaskFilter = previousFitterFilter?.subtasks?.find(
      (subtask) => subtask.id === subtaskId
    );

    let usedSubtasks: Task[] = [];
    if (options?.techSubtasks) {
      const previousTechniciansFilter = previousSubtaskFilter?.subtasks;

      usedSubtasks = this.getTechnicianDefaultFilters(
        fitter,
        previousTechniciansFilter
      );
    }
    if (options?.interventionVehicleSubtasks) {
      const previousInterventionVehiclesFilter =
        previousSubtaskFilter?.subtasks;

      usedSubtasks = this.getInterventionVehiclesDefaultFilters(
        fitter,
        previousInterventionVehiclesFilter
      );
    }

    const resultSubfilter: Task = {
      id: subtaskId,
      name: this.translator.getInstantTranslation(
        options?.taskName ||
          options?.techName ||
          options?.interventionVehicleName ||
          options?.rvName ||
          `PlanningFilters.filters.${resType}`
      ),
      checked:
        previousSubtaskFilter?.checked ?? previousFitterFilter?.checked ?? true,
      subtaskopened: false,
      subtasks: usedSubtasks,
    };

    return resultSubfilter;
  }

  generateFiltersFromFitters(
    fitters: Fitter[],
    previousFilters?: Task[],
    forceTechView?: boolean, // true = force tech detailed view ; false = force tech condensed view ; undefined = no force
    forceInterventionVehicleView?: boolean, // true = force interventionVehicle detailed view ; false = force interventionVehicle condensed view ; undefined = no force
    forceRVView?: boolean // true = force RV detailed view ; false = force RV condensed view ; undefined = no force
  ): Task[] {
    if (!fitters) return [];

    const usedPreviousFilters =
      previousFilters?.length > 0
        ? previousFilters
        : this.getStoredResourcesFilters();

    let isTechView =
      this.planningCalendarService.filtersAreTechnicianView(
        usedPreviousFilters
      );

    if (forceTechView !== undefined) {
      isTechView = forceTechView;
    }

    let isInterventionVehicleView =
      this.planningCalendarService.filtersAreInterventionVehicleView(
        usedPreviousFilters
      );

    if (forceInterventionVehicleView !== undefined) {
      isInterventionVehicleView = forceInterventionVehicleView;
    }

    let isRVView =
      this.planningCalendarService.filtersAreRVView(usedPreviousFilters);

    if (forceRVView !== undefined) {
      isRVView = forceRVView;
    }

    const monoCenter = !!this.storage.getItem<
      JsonObjectKey,
      CalendarViewParams
    >('calendar-filter-vue', localStorage, {
      detailedTechCenter: false,
      detailedInterventionVehicle: false,
      detailedRV: false,
      monoCenter: true,
    })?.monoCenter;

    const filters: Task[] = [];
    let fitterCheckedId: string;
    if (monoCenter) {
      fitterCheckedId =
        fitters.find(
          (fitter) =>
            usedPreviousFilters?.find((task) => task.id === fitter.id)?.checked
        )?.id || fitters[0]?.id;
    }

    fitters.forEach((fitter) => {
      const previousFitterFilter = usedPreviousFilters?.find(
        (task) => task.id === fitter.id
      );

      const filter: Task = {
        id: fitter.id,
        name: this.fitterNameDisplayService.getInstantFitterNameTranslation(
          fitter
        ),
        checked: monoCenter
          ? fitter.id === fitterCheckedId
          : previousFitterFilter?.checked ?? true,
        subtasks: [],
      };

      // For each resource type, generate the filter(s) that correspond to it as a subfilter of the fitter
      Object.values(ResourceTypeEnum).forEach((resourceType) => {
        const canDoCurrentIntervention = this.canDoIntervention(
          fitter,
          resourceType
        );

        if (canDoCurrentIntervention) {
          if (resourceType === 'OUTSIDE' && !isInterventionVehicleView) {
            const generatedSubtask = this.generateFitterSubfilter(
              fitter,
              resourceType,
              previousFitterFilter,
              { interventionVehicleSubtasks: true }
            );
            filter.subtasks.push(generatedSubtask);
          } else if (
            resourceType === 'INTERVENTION_VEHICLE' &&
            isInterventionVehicleView
          ) {
            fitter.interventionVehicles
              ?.filter((interventionVehicle) => interventionVehicle?.active)
              ?.forEach((interventionVehicle) => {
                const generatedSubtask = this.generateFitterSubfilter(
                  fitter,
                  resourceType,
                  previousFitterFilter,
                  { interventionVehicleName: interventionVehicle.name }
                );
                filter.subtasks.push(generatedSubtask);
              });

            // Always generate a "not attributed" pseudo-interventionVehicle column
            const generatedSubtask = this.generateFitterSubfilter(
              fitter,
              resourceType,
              previousFitterFilter,
              {
                taskName:
                  fitter?.filterLabels?.interventionVehicleLabel ??
                  'PlanningFilters.filters.notSetInterventionVehicle',
              }
            );
            filter.subtasks.push(generatedSubtask);
          } else if (resourceType === 'REPLACEMENTVEHICLE' && !isRVView) {
            const generatedSubtask = this.generateFitterSubfilter(
              fitter,
              resourceType,
              previousFitterFilter
            );
            filter.subtasks.push(generatedSubtask);
          } else if (resourceType === 'RV_DETAILED' && isRVView) {
            fitter.vehicles
              ?.filter((res) => res?.active)
              ?.forEach((tech) => {
                const generatedSubtask = this.generateFitterSubfilter(
                  fitter,
                  resourceType,
                  previousFitterFilter,
                  { rvName: tech.id + '##' + tech.name }
                );
                filter.subtasks.push(generatedSubtask);
              });

            // Always generate a "not attributed" pseudo-rv column
            const generatedSubtask = this.generateFitterSubfilter(
              fitter,
              resourceType,
              previousFitterFilter,
              {
                taskName:
                  fitter?.filterLabels?.replacementVehicleLabel ??
                  'PlanningFilters.filters.notSetRV',
              }
            );
            filter.subtasks.push(generatedSubtask);
          } else if (resourceType === 'IN_CENTER' && !isTechView) {
            const generatedSubtask = this.generateFitterSubfilter(
              fitter,
              resourceType,
              previousFitterFilter,
              { techSubtasks: true }
            );
            filter.subtasks.push(generatedSubtask);
          } else if (resourceType === 'TECHNICIAN' && isTechView) {
            fitter.resources
              ?.filter((res) => res?.active)
              ?.forEach((tech) => {
                const generatedSubtask = this.generateFitterSubfilter(
                  fitter,
                  resourceType,
                  previousFitterFilter,
                  { techName: tech.name }
                );
                filter.subtasks.push(generatedSubtask);
              });

            // Always generate a "not attributed" pseudo-technician column
            const generatedSubtask = this.generateFitterSubfilter(
              fitter,
              resourceType,
              previousFitterFilter,
              {
                taskName:
                  fitter?.filterLabels?.technicianLabel ??
                  'PlanningFilters.filters.notSetTechnician',
              }
            );
            filter.subtasks.push(generatedSubtask);
          }
        }
      });

      // Lesser priorities go to the left of the calendar
      const columnDefaultPriorities: Record<ResourceType, number> = {
        TECHNICIAN: 0,
        IN_CENTER: 1,
        INTERVENTION_VEHICLE: 2,
        OUTSIDE: 3,
        RV_DETAILED: 4,
        REPLACEMENTVEHICLE: 5,
      };

      // Sort subtasks based on previously known order or default column order
      filter.subtasks.sort((subtask1, subtask2) => {
        if (previousFitterFilter?.subtasks?.length > 0) {
          const foundPreviousSubtask1Index =
            previousFitterFilter?.subtasks?.findIndex(
              (subTask) => subTask.id === subtask1.id
            );
          const foundPreviousSubtask2Index =
            previousFitterFilter?.subtasks?.findIndex(
              (subTask) => subTask.id === subtask2.id
            );

          // If we know the previous order of both, only then can we use it. Otherwise we could confuse the order of columns like IN_CENTER and OUTSIDE in some edge cases
          if (
            foundPreviousSubtask1Index > -1 &&
            foundPreviousSubtask2Index > -1
          ) {
            return foundPreviousSubtask1Index - foundPreviousSubtask2Index;
          }
        }

        // Use the default column order if we couldn't figure out the previous order
        let subtask1Prio = 10;
        let subtask2Prio = 10;
        Object.keys(columnDefaultPriorities).forEach((colName) => {
          if (subtask1.id?.includes(colName)) {
            subtask1Prio = columnDefaultPriorities[colName];
          }
          if (subtask2.id?.includes(colName)) {
            subtask2Prio = columnDefaultPriorities[colName];
          }
        });

        return subtask1Prio - subtask2Prio;
      });

      filters.push(filter);
    });

    return filters;
  }

  buildTechRadioSubtaskId(fitterId: string, techName: string) {
    return `${fitterId}_${techName}_technician`;
  }
  buildInterventionVehicleRadioSubtaskId(fitterId: string, techName: string) {
    return `${fitterId}_${techName}_interventionVehicle`;
  }

  getTechnicianDefaultFilters(
    fitter: Fitter,
    previousTechniciansFilter: Task[]
  ) {
    const techs = [];

    const allTechId = this.buildTechRadioSubtaskId(
      fitter?.id,
      PlanningSandboxService.allTechniciansFilterKey
    );
    techs?.push({
      id: allTechId,
      name: this.translator.getInstantTranslation(
        `PlanningFilters.filters.allTechnicians`
      ),
      checked:
        previousTechniciansFilter?.find((tech) => tech?.id === allTechId)
          ?.checked ?? true,
    });

    const notSetTechId = this.buildTechRadioSubtaskId(
      fitter?.id,
      PlanningSandboxService.notSetTechniciansFilterKey
    );
    techs?.push({
      id: notSetTechId,
      name: this.translator.getInstantTranslation(
        `PlanningFilters.filters.notSetTechnician`
      ),
      checked:
        previousTechniciansFilter?.find((tech) => tech?.id === notSetTechId)
          ?.checked ?? false,
    });

    fitter?.resources
      ?.filter((res) => res?.active)
      .forEach((resource) => {
        const techId = this.buildTechRadioSubtaskId(fitter?.id, resource.name);
        techs.push({
          id: techId,
          name: resource.name,
          checked:
            previousTechniciansFilter?.find((tech) => tech?.id === techId)
              ?.checked ?? false,
        });
      });
    return techs;
  }

  getInterventionVehiclesDefaultFilters(
    fitter: Fitter,
    previousInterventionVehiclesFilter: Task[]
  ) {
    const interventionVehicles = [];

    const allinterventionVehiclesId =
      this.buildInterventionVehicleRadioSubtaskId(
        fitter?.id,
        PlanningSandboxService.allInterventionVehiclesFilterKey
      );
    interventionVehicles?.push({
      id: allinterventionVehiclesId,
      name: this.translator.getInstantTranslation(
        `PlanningFilters.filters.allInterventionVehicles`
      ),
      checked:
        previousInterventionVehiclesFilter?.find(
          (interventionVehicle) =>
            interventionVehicle?.id === allinterventionVehiclesId
        )?.checked ?? true,
    });

    const notSetInterventionVehicleId =
      this.buildInterventionVehicleRadioSubtaskId(
        fitter?.id,
        PlanningSandboxService.noSetInterventionVehiclesFilterKey
      );
    interventionVehicles?.push({
      id: notSetInterventionVehicleId,
      name: this.translator.getInstantTranslation(
        `PlanningFilters.filters.notSetInterventionVehicle`
      ),
      checked:
        previousInterventionVehiclesFilter?.find(
          (interventionVehicle) =>
            interventionVehicle?.id === notSetInterventionVehicleId
        )?.checked ?? false,
    });

    fitter?.interventionVehicles
      ?.filter((interventionVehicle) => interventionVehicle?.active)
      .forEach((interventionVehicle) => {
        const interventionVehicleId =
          this.buildInterventionVehicleRadioSubtaskId(
            fitter?.id,
            interventionVehicle.name
          );
        interventionVehicles.push({
          id: interventionVehicleId,
          name: interventionVehicle.name,
          checked:
            previousInterventionVehiclesFilter?.find(
              (task) => task?.id === interventionVehicleId
            )?.checked ?? false,
        });
      });
    return interventionVehicles;
  }

  getMonoCenterSelectedFitter() {
    let fitterId = null;
    const monoCenter = !!this.storage.getItem<
      JsonObjectKey,
      CalendarViewParams
    >('calendar-filter-vue', localStorage, {
      detailedTechCenter: false,
      detailedInterventionVehicle: false,
      detailedRV: false,
      monoCenter: true,
    })?.monoCenter;
    const filters = this.storage.getItem<JsonListKey, Task>(
      'agenda-resource-filters'
    );

    if (monoCenter) {
      fitterId = filters?.filter((task) => task?.checked)[0]?.id;
    }

    return fitterId;
  }

  private getResources(fitters: Fitter[]): Observable<Resource[]> {
    const resources: Resource[] = [];

    let previousFilters = this.getStoredResourcesFilters();

    // If there is a fitter with no filter stored, then it means it's was just added by the admin
    // and the filter component was not fast enough to update the store before this line and we should count it in
    const fittersNotInFilterStore =
      fitters?.filter(
        (fitter) =>
          previousFilters.findIndex((filter) => filter.id === fitter.id) === -1
      ) || [];
    if (fittersNotInFilterStore.length > 0) {
      previousFilters.push(
        ...this.generateFiltersFromFitters(
          fittersNotInFilterStore,
          previousFilters
        )
      );
    }

    // In technician view, if a technician was modified/added/removed just before
    // it is reflected in the fitters but not in the existing resources so we must rebuild and replace those (idem for intervention vehicle and replacement vehicle)
    if (
      this.planningCalendarService.filtersAreTechnicianView(previousFilters) ||
      this.planningCalendarService.filtersAreInterventionVehicleView(
        previousFilters
      ) ||
      this.planningCalendarService.filtersAreRVView(previousFilters)
    ) {
      const fittersToUpdate = fitters.filter((fitter) => {
        // Check if technicians are not syncronized
        const existingTechFilters = previousFilters
          .find((previousFilter) => previousFilter.id === fitter.id)
          .subtasks.filter(
            (previousFilter) =>
              this.resManagerService.getResourceType(previousFilter.id) ===
                'TECHNICIAN' &&
              !this.resManagerService.isDefaultTechnician(previousFilter.id)
          )
          .map((previousFilter) =>
            this.resManagerService.getResourceTechnician(previousFilter.id)
          );

        const currentFitterTechs =
          fitter.resources
            ?.filter((res) => res.active)
            ?.map((res) => res.name) || [];

        const hasUpdatedTech =
          currentFitterTechs.findIndex(
            (currentFitterTech) =>
              !existingTechFilters.includes(currentFitterTech)
          ) > -1;

        // Check if interventionVehicles are not syncronized
        const existingInterventionVehiclesFilters = previousFilters
          .find((previousFilter) => previousFilter.id === fitter.id)
          .subtasks.filter(
            (previousFilter) =>
              this.resManagerService.getResourceType(previousFilter.id) ===
                'INTERVENTION_VEHICLE' &&
              !this.resManagerService.isDefaultInterventionVehicle(
                previousFilter.id
              )
          )
          .map((previousFilter) =>
            this.resManagerService.getResourceInterventionVehicle(
              previousFilter.id
            )
          );

        const currentFitterInterventionVehicles =
          fitter.interventionVehicles
            ?.filter((res) => res.active)
            ?.map((res) => res.name) || [];

        const hasUpdatedinterventionVehicle =
          currentFitterInterventionVehicles.findIndex(
            (currentFitterInterventionVehicle) =>
              !existingInterventionVehiclesFilters.includes(
                currentFitterInterventionVehicle
              )
          ) > -1;

        // Check if RV are not syncronized
        const existingRVFilters = previousFilters
          .find((previousFilter) => previousFilter.id === fitter.id)
          .subtasks.filter(
            (previousFilter) =>
              this.resManagerService.getResourceType(previousFilter.id) ===
                'RV_DETAILED' &&
              !this.resManagerService.isDefaultRV(previousFilter.id)
          )
          .map((previousFilter) =>
            this.resManagerService.getResourceRV(previousFilter.id)
          );

        const currentFitterRVs =
          fitter.vehicles
            ?.filter((res) => res.active)
            ?.map((res) => res?.id + '##' + res.name) || [];

        const hasUpdatedRV =
          currentFitterRVs.findIndex(
            (currentFitterRV) => !existingRVFilters.includes(currentFitterRV)
          ) > -1;

        return hasUpdatedTech || hasUpdatedinterventionVehicle || hasUpdatedRV;
      });

      if (fittersToUpdate.length > 0) {
        const filtersToUpdate = this.generateFiltersFromFitters(
          fittersToUpdate,
          previousFilters
        );

        previousFilters = previousFilters.filter(
          (previousFilter) =>
            filtersToUpdate.findIndex(
              (filterToUpdate) => filterToUpdate.id === previousFilter.id
            ) === -1
        );
        previousFilters.push(...filtersToUpdate);
      }
    }

    previousFilters.sort((filter1, filter2) => {
      const foundFitter1Index = fitters.findIndex((f) => f.id === filter1.id);
      const foundFitter2Index = fitters.findIndex((f) => f.id === filter2.id);

      return foundFitter1Index - foundFitter2Index;
    });
    previousFilters.forEach((previousFitterFilter) => {
      const foundFitter = fitters?.find(
        (f) => f.id === previousFitterFilter.id
      );
      if (!foundFitter) {
        return;
      }

      // Here, subtasks are considered to already be sorted
      previousFitterFilter.subtasks.forEach((subtask) => {
        const resType = this.resManagerService.getResourceType(subtask.id);
        let resName: string;
        if (resType === 'TECHNICIAN') {
          resName = this.resManagerService.getResourceTechnician(subtask.id);
        } else if (resType === 'INTERVENTION_VEHICLE') {
          resName = this.resManagerService.getResourceInterventionVehicle(
            subtask.id
          );
        } else if (resType === 'RV_DETAILED') {
          resName = this.resManagerService.getResourceRV(subtask.id);
        }

        const canDoCurrentIntervention = this.canDoIntervention(
          foundFitter,
          resType
        );

        if (canDoCurrentIntervention) {
          const resource =
            this.planningCalendarService.constructCalendarResource(
              foundFitter,
              resType,
              resName,
              (previousFitterFilter?.checked && subtask?.checked) ?? true
            );
          resources.push(resource);
        }
      });
    });

    this.storeResources(resources);
    return of(resources);
  }

  async updateResourcesToDisplay(resourcesToDisplay: string[]) {
    const dossiersToValidate = [];
    this.resources$.pipe(take(1)).subscribe((resources) => {
      if (resources?.length) {
        const resourcesToStore = resources.map((resource) => {
          return {
            ...resource,
            display:
              resourcesToDisplay.findIndex(
                (resourceToDisplay) => resource.id === resourceToDisplay
              ) !== -1,
          };
        });
        const filters = this.storage.getItem<JsonListKey, Task>(
          'agenda-resource-filters'
        );
        this.updateEventsToDisplay(filters);
        resourcesToStore.sort(
          (res1, res2) =>
            resourcesToDisplay.indexOf(res1.id) -
            resourcesToDisplay.indexOf(res2.id)
        );
        this.storeResources(resourcesToStore);
        const fitterIds = [
          ...new Set(
            resourcesToStore?.map((resource) => {
              if (resource.display && !!resource.fitterId) {
                return resource.fitterId;
              }
            }) ?? []
          ),
        ]?.filter((fitterId) => !!fitterId);
        if (fitterIds.length) {
          this.getDossiersToValidate(fitterIds).subscribe(
            (dossiers: Dossier[]) => {
              this.isLoadingEvents$.next(false);
              dossiers.forEach((dossier: Dossier) => {
                if (!dossier?.appointment?.isValidatedByFitter) {
                  dossiersToValidate.push(dossier);
                }
              });
              this.storeDossiersToValidate(dossiersToValidate);
            },
            (error: HttpErrorResponse) => {
              this.isLoadingEvents$.next(false);
              this.snackbarService.showError(
                this.translator.getInstantTranslation('Snackbar.errorTitle'),
                error?.error?.message ||
                  this.translator.getInstantTranslation(
                    'Planning.fetchPlanningDossiersError'
                  )
              );

              console.error(error);
            }
          );

          this.agendaHttpService
            .getRequestApmtDossiers({
              fitterIds,
            })
            .subscribe(
              (dossiers: Dossier[]) => {
                this.storeDossiersRequestedApmt(dossiers);
              },
              (error: HttpErrorResponse) => {
                this.snackbarService.showError(
                  this.translator.getInstantTranslation('Snackbar.errorTitle'),
                  error?.error?.message ||
                    this.translator.getInstantTranslation(
                      'Planning.fetchPlanningDossiersError'
                    )
                );

                console.error(error);
              }
            );
        }
      }
    });
  }

  updateEventsToDisplay(filters: Task[]) {
    const events = this.getAgendaEventsSnapshot()?.map((event) => {
      let extendedProps;
      let shouldDisplay: boolean;

      if (
        this.resManagerService.isDossierEventExtendedProps(event?.extendedProps)
      ) {
        extendedProps = event?.extendedProps as DossierEventExtendedProps;
        const eventFitter = extendedProps?.fitter as Fitter;
        const eventDossier = extendedProps?.dossier as Dossier;

        shouldDisplay = this.planningCalendarService?.shouldDisplayDossierEvent(
          filters,
          eventDossier,
          eventFitter
        );
      } else {
        extendedProps = event?.extendedProps as GenericEventExtendedProps;
        const eventFitter = extendedProps?.fitter as Fitter;
        const eventEvent = extendedProps.event as AgendaEvent;
        const resourceType = this.resManagerService.getResourceType(
          event.resourceId
        );
        const location =
          resourceType === 'IN_CENTER' || resourceType === 'TECHNICIAN'
            ? 'center'
            : 'outside';

        shouldDisplay = this.planningCalendarService?.shouldDisplayAgendaEvent(
          filters,
          eventEvent,
          eventFitter,
          location
        );
      }

      return {
        ...event,
        extendedProps: {
          ...extendedProps,
          display: shouldDisplay,
        },
      };
    });

    if (events?.length) {
      this.replaceAgendaEvents(
        events?.map(
          (event) =>
            event?.extendedProps?.dossier?.id || event?.extendedProps?.event?.id
        ),
        events
      );
    }
  }

  @Dispatch()
  storeResources(resources: Resource[]): Observable<SetResourcesAction> {
    return of(new SetResourcesAction(resources));
  }

  getOpenDossierObservable() {
    return this.eventSubHttpService.subscribeToOpenDossier();
  }

  getCloseDossierObservable() {
    return this.eventSubHttpService.subscribeToCloseDossier();
  }

  getLastPingObservable() {
    return this.eventSubHttpService.subscribeToLastPing();
  }

  sendOpenDossierNotif(dossierId: string) {
    return this.dossierManagerService.sendOpenDossierNotif(dossierId);
  }

  sendCloseDossierNotif(dossierId: string) {
    return this.dossierManagerService.sendCloseDossierNotif(dossierId);
  }

  bookRequestedAppointment(dossier: Dossier) {
    // We call createAgendaDossier because we need to book the appointment, not modify it
    return this.createAgendaDossier(dossier, false).pipe(
      tap((result) => this.updatePlanningDossierChange(result.dossier))
    );
  }

  getDossiersToValidate(fitterIds: string[]) {
    return this.agendaHttpService.getDossiersToValidate({
      fitterIds,
    });
  }

  private getEvents(
    fitters: Fitter[],
    resources: Resource[],
    begin: string,
    end: string
  ): Observable<any[]> {
    this.isLoadingEvents$.next(true);

    const fitterIds = [
      ...new Set(resources?.map((resource) => resource.fitterId) ?? []),
    ];

    return combineLatest([
      this.agendaHttpService.getDossiers({
        fitterIds,
        status: ['NEW', 'PROGRAMMED', 'COMPLETED', 'CANCELLED', 'NO_SHOW'],
        filters: {
          startDate: begin,
          endDate: end,
          isValidatedByFitter: true,
        } as DossierAgendaFiltersInput,
      }),
      this.agendaHttpService.getEvents({ fitterIds }),
      this.getDossiersToValidate(fitterIds),
      this.agendaHttpService.getRequestApmtDossiers({ fitterIds }),
    ]).pipe(
      take(1),
      tap(
        ([dossiers, events, dossiersToValidate, dossiersRequestApmt]: [
          Dossier[],
          AgendaEvent[],
          Dossier[],
          Dossier[]
        ]) => {
          this.isLoadingEvents$.next(false);

          const agendaEvents: EventApi[] = [];
          dossiers.forEach((dossier: Dossier) => {
            if (dossier?.appointment?.isValidatedByFitter) {
              const newDossiersEvents =
                this.planningCalendarService.constructAllCalendarDossierEvents(
                  dossier,
                  fitters
                );

              agendaEvents.push(...newDossiersEvents);
            }
          });
          this.storeDossiersToValidate(dossiersToValidate);

          events.forEach((event) => {
            const newGenericEvents =
              this.planningCalendarService.constructAllCalendarGenericEvent(
                event,
                fitters,
                {
                  start: dayjs(this.currentTimeFrameStart),
                  end: dayjs(this.currentTimeFrameEnd),
                }
              );

            agendaEvents.push(...newGenericEvents);
          });

          this.storeAgendaEvents(agendaEvents);

          this.storeDossiersRequestedApmt(dossiersRequestApmt);
        }
      )
    );
  }

  updateEventsFromDossier(updatedDossier: Dossier, fitters: Fitter[]) {
    const agendaEvents: EventApi[] = [];
    let resultObs: Observable<any> = of(null);

    if (
      updatedDossier?.requestAppointment?.status === 'CANCELLED' &&
      updatedDossier?.state === 'CANCELLED'
    ) {
      resultObs = resultObs.pipe(
        switchMap(() => this.removeDossiersRequestApmt([updatedDossier.id]))
      );
    } else if (updatedDossier?.appointment?.isValidatedByFitter) {
      // If the dossier is validated, show it on the calendar with events
      const newDossiersEvents =
        this.planningCalendarService.constructAllCalendarDossierEvents(
          updatedDossier,
          fitters
        );

      agendaEvents.push(...newDossiersEvents);

      // Adding a process that removes the dossier from the planning basket since it is on calendar
      resultObs = resultObs.pipe(
        // Remove it from externalDossiers list and from appointmentRequest list
        switchMap(() =>
          forkJoin([
            this.removeDossiersToValidate([updatedDossier.id]),
            this.removeDossiersRequestApmt([updatedDossier.id]),
          ])
        )
      );
    } else if (updatedDossier?.appointment?.time) {
      // If not validated but appointment has infos, then add it to the basket on externalDossiers
      resultObs = resultObs.pipe(
        switchMap(() => this.replaceDossiersToValidate([updatedDossier]))
      );
    } else if (
      !!updatedDossier?.requestAppointment &&
      !updatedDossier?.appointment
    ) {
      // If not validated but has requestAppointment set, then add it to the basket on requestAppointment
      resultObs = resultObs.pipe(
        switchMap(() => this.replaceDossiersRequestedApmt([updatedDossier]))
      );
    }

    // Finally, replace the calendar events with the previously built list
    // (empty if not on calendar, to delete all previous events of this dossier)
    return resultObs.pipe(
      switchMap(() => this.replaceAgendaEvent(updatedDossier.id, agendaEvents))
    );
  }

  getSpecialClosingEvents(resources: Resource[]) {
    const fitterIds = [
      ...new Set(resources?.map((resource) => resource.fitterId) ?? []),
    ];
    return this.agendaHttpService.getEvents({
      fitterIds,
      eventType: 'SPECIAL_CLOSING',
    });
  }

  @Dispatch()
  storeAgendaEvents(events: EventApi[]): Observable<SetAgendaEventsAction> {
    return of(new SetAgendaEventsAction(events));
  }

  @Dispatch()
  addAgendaEvents(events: EventApi[]): Observable<AddAgendaEventsAction> {
    return of(new AddAgendaEventsAction(events));
  }

  @Dispatch()
  replaceAgendaEvent(
    eventId: string,
    newEvents: EventApi[]
  ): Observable<ReplaceAgendaEventAction> {
    return of(
      new ReplaceAgendaEventAction({ idToReplace: eventId, events: newEvents })
    );
  }

  @Dispatch()
  replaceAgendaEvents(
    eventIds: string[],
    newEvents: EventApi[]
  ): Observable<ReplaceAgendaEventsAction> {
    return of(
      new ReplaceAgendaEventsAction({
        idsToReplace: eventIds,
        events: newEvents,
      })
    );
  }

  @Dispatch()
  storeDossiersToValidate(
    dossiers: Dossier[]
  ): Observable<SetDossiersToValidateAction> {
    return of(new SetDossiersToValidateAction(dossiers));
  }

  @Dispatch()
  storeNewDossiersToValidate(
    dossiers: Dossier[]
  ): Observable<SetNewDossiersToValidateAction> {
    return of(new SetNewDossiersToValidateAction(dossiers));
  }

  @Dispatch()
  replaceDossiersToValidate(
    dossiers: Dossier[]
  ): Observable<ReplaceDossiersToValidateAction> {
    return of(new ReplaceDossiersToValidateAction(dossiers));
  }

  removeDossiersToValidate(dossierIdList: string[]) {
    return this.externalDossiersToValidate$.pipe(
      take(1),
      switchMap((dossiersToValidate) => {
        const updatedList = dossiersToValidate?.filter(
          (knownDossier) => !dossierIdList.includes(knownDossier.id)
        );

        if (updatedList?.length !== dossiersToValidate?.length) {
          return this.storeDossiersToValidate(updatedList);
        }

        return of(null);
      })
    );
  }

  @Dispatch()
  storeDossiersRequestedApmt(
    dossiers: Dossier[]
  ): Observable<SetDossiersRequestedAppointmentAction> {
    return of(new SetDossiersRequestedAppointmentAction(dossiers));
  }

  @Dispatch()
  storeNewDossierRequestedApmt(
    dossier: Dossier
  ): Observable<SetNewDossierRequestedAppointmentAction> {
    return of(new SetNewDossierRequestedAppointmentAction(dossier));
  }

  @Dispatch()
  replaceDossiersRequestedApmt(
    dossiers: Dossier[]
  ): Observable<ReplaceDossiersRequestedAppointmentAction> {
    return of(new ReplaceDossiersRequestedAppointmentAction(dossiers));
  }

  removeDossiersRequestApmt(dossierIdList: string[]) {
    return this.externalAppointmentRequests$.pipe(
      take(1),
      switchMap((dossiersRequestApmt) => {
        const updatedList = dossiersRequestApmt?.filter(
          (knownDossier) => !dossierIdList.includes(knownDossier.id)
        );

        if (updatedList?.length !== dossiersRequestApmt?.length) {
          return this.storeDossiersRequestedApmt(updatedList);
        }

        return of(null);
      })
    );
  }

  fetchDossier(id: string): Observable<Dossier> {
    return this.dossierHttpService.fetchDossier({ id }).pipe(take(1));
  }

  createAgendaDossier(dossier: Dossier, deleteId = true) {
    return this.storageHttpService
      .uploadDocuments(dossier?.claim?.documents ?? [])
      .pipe(
        tap((documents) => {
          dossier.claim.documents = documents;
        }),
        switchMap(() => {
          if (deleteId) {
            delete dossier.id;
          }
          return this.agendaHttpService.bookAppointment({ dossier });
        })
      );
  }

  updateAutoCompleteCarSelection(car: Car) {
    this.autoCompleteCarSelection$.next(car);
  }

  @Dispatch()
  fetchCarData(licensePlateNumber: string, damagedPart?: string) {
    return this.carHttpService
      .getCarData({ licensePlateNumber, damagedPart })
      .pipe(
        take(1),
        tap((vehicle: Car) => {
          if (!vehicle || !vehicle.manufacturer) {
            throw throwError('License Plate not found');
          }
        }),
        map((car: Car) => {
          return new PatchClaimAction({
            car: {
              ...car,
              licensePlateNumber: LicensePlateNumberMask.mask(
                licensePlateNumber,
                this.configurationService.getAllLicensePlateCountries()
              ),
              countryCode:
                this.configurationService.getDefaultLicensePlateCountry(),
            },
          });
        })
      );
  }

  getPlacesFromAddress(address: string): Observable<LocationData[]> {
    return this.locationHttpService.getPlacesFromAddress(address);
  }

  getGeoCodeFromPlaceId(placeId: string): Observable<LocationDataGeocoded> {
    return this.locationHttpService.getGeoCodeFromPlaceId(placeId);
  }

  getConfigService() {
    return this.configurationService;
  }

  getAccountLogin() {
    return this.authOktaService?.getUserLogin$();
  }

  updateAgendaDossier(dossier: Dossier) {
    return this.storageHttpService
      .uploadDocuments(dossier?.claim?.documents ?? [])
      .pipe(
        tap((documents) => {
          dossier.claim = {
            ...dossier.claim,
            documents,
          };
        }),
        switchMapTo(this.agendaHttpService.modifyAppointment({ dossier }))
      );
  }

  abandonDossier(dossier: Dossier) {
    return this.storageHttpService
      .uploadDocuments(dossier?.claim?.documents ?? [])
      .pipe(
        tap((documents) => {
          dossier.claim = {
            ...dossier.claim,
            documents,
          };
        }),
        switchMapTo(
          this.agendaHttpService.abandonDossier({
            id: dossier?.id,
            comment: getLatestDossierCommentTextByType(dossier, [
              DossierCommentsEnum.abandon,
            ]),
          })
        )
      );
  }

  cancelAppointment(params: CancelAppointmentApi) {
    return this.agendaHttpService.cancelAppointment(params).pipe(take(1));
  }

  notifyAppointment(params: NotifyAppointmentApi) {
    return this.agendaHttpService.notifyAppointment(params).pipe(take(1));
  }

  fetchEvent(id: string): Observable<AgendaEvent> {
    return this.agendaHttpService.getEvent({ id }).pipe(take(1));
  }

  createEvent(event: AgendaEvent): Observable<AgendaEvent> {
    return this.agendaHttpService.createEvent({ event }).pipe(take(1));
  }

  bulkCreateEvent(events: AgendaEvent[]): Observable<AgendaEvent[]> {
    return this.agendaHttpService.bulkCreateEvent({ events }).pipe(take(1));
  }

  updateEvent(event: AgendaEvent): Observable<AgendaEvent> {
    delete event.creationData;
    delete event.createDate;
    delete event.updateDate;
    return this.agendaHttpService.updateEvent({ event }).pipe(take(1));
  }

  deleteEvent(id: string): Observable<boolean> {
    return this.agendaHttpService.deleteEvent({ id }).pipe(take(1));
  }

  updateFitterComment(fitterId: string, comment: string) {
    return this.fittersHttpService
      .updateFitterComment(fitterId, comment)
      .pipe(take(1));
  }

  canDoIntervention(fitter: Fitter, resourceType: ResourceType) {
    if (this.resManagerService.isAnyCenterRes(resourceType)) {
      return this.canDoInCenterIntervention(fitter);
    } else if (this.resManagerService.isAnyOutsideRes(resourceType)) {
      return this.canDoOutsideIntervention(fitter);
    } else if (this.resManagerService.isAnyRVRes(resourceType)) {
      return this.canShareReplacementVehicle(fitter);
    }

    return false;
  }

  canDoInCenterIntervention(fitter: Fitter): boolean {
    return (
      fitter?.serviceTypes?.findIndex(
        (service) => service.active && service.canMakeInCenterIntervention
      ) !== -1
    );
  }

  interventionAllowsInCenter(interventionType: FitterServiceType) {
    return interventionType.canMakeInCenterIntervention;
  }

  canDoOutsideIntervention(fitter: Fitter): boolean {
    return (
      fitter?.serviceTypes?.findIndex(
        (service) => service.active && service.canMakeOutsideIntervention
      ) !== -1
    );
  }

  interventionAllowsOutside(interventionType: FitterServiceType) {
    return interventionType.canMakeOutsideIntervention;
  }

  canDoConveyingIntervention(fitter: Fitter): boolean {
    return (
      fitter?.serviceTypes?.findIndex(
        (service) => service.active && service.canMakeInCenterIntervention
      ) !== -1
    );
  }

  interventionAllowsConveying(interventionType: FitterServiceType) {
    return interventionType.canMakeInCenterIntervention;
  }

  canShareReplacementVehicle(fitter: Fitter): boolean {
    return (
      fitter?.serviceTypes?.findIndex(
        (service) => service.active && service.canShareReplacementVehicle
      ) !== -1
    );
  }

  isAppointmentFinished(a: Appointment) {
    return ['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(a?.status);
  }

  isEventSpecialClosing(event: AgendaEvent) {
    return event?.type === 'SPECIAL_CLOSING';
  }

  isDossierFinished(d: Dossier) {
    return ['COMPLETE', 'CANCELLED', 'DONE', 'NO_SHOW', 'ABANDONED'].includes(
      d?.state
    );
  }

  isDossierActive(d: Dossier) {
    return (
      !['ABANDONED', 'CANCELLED'].includes(d?.state) &&
      d?.appointment?.status !== 'CANCELLED' &&
      d?.requestAppointment?.status !== 'CANCELLED'
    );
  }

  isDossierDraft(d: Dossier) {
    // Draft dossiers always have a draft appointment with null id
    return !!d?.appointment && !d?.appointment?.id;
  }

  eventsHaveBlockingAttributes(
    dossier: Dossier,
    events: EventApi[] /* calendar events containing extended props */,
    options: {
      checks: (
        | 'vehicle'
        | 'tech'
        | 'interventionVehicle'
        | 'overlaps'
        | 'leave_event'
      )[];
      vehicle?: FitterVehicle;
      tech?: FitterResource;
      interventionVehicle?: FitterResource;
    }
  ): boolean {
    const shouldCheckVehicles = options.checks.includes('vehicle');
    const shouldCheckTechs = options.checks.includes('tech');
    const shouldCheckinterventionVehicles = options.checks.includes(
      'interventionVehicle'
    );
    const shouldCheckOverlaps = options.checks.includes('overlaps');
    const shouldCheckLeaves = options.checks.includes('leave_event');

    // Here we do some early checks in order to avoid tedious checking on all events
    const filteredEvents = events.filter((event) => {
      const extendedProps = event?.extendedProps as EventExtendedProps;
      const eventFitter = extendedProps?.fitter;

      const isDossierEvent =
        this.resManagerService.isDossierEventExtendedProps(extendedProps);
      const isAgendaEvent =
        this.resManagerService.isGenericEventExtendedProps(extendedProps);

      if (eventFitter.id !== dossier.appointment?.fitterId) {
        false;
      }
      if (isDossierEvent) {
        if (!shouldCheckOverlaps) {
          return false;
        }

        const eventDossier = extendedProps?.dossier;

        // Drafts are non-blocking
        if (this.isDossierDraft(eventDossier)) {
          return false;
        }

        // If the event is the one corresponding to the updated dossier, then skip
        if (dossier?.id === eventDossier?.id) {
          return false;
        }

        // If the event does not have the same fitter as the dossier, then skip
        if (
          eventDossier?.appointment?.fitterId !== dossier?.appointment?.fitterId
        ) {
          return false;
        }
      } else if (isAgendaEvent) {
        if (!shouldCheckLeaves) {
          return false;
        }

        const agendaEvent = extendedProps?.event;

        if (!shouldCheckTechs && agendaEvent?.type === 'TECH_LEAVE') {
          return false;
        }

        if (!shouldCheckVehicles && agendaEvent?.type === 'RV_LEAVE') {
          return false;
        }

        if (
          !shouldCheckinterventionVehicles &&
          agendaEvent?.type === 'INTERVENTION_VEHICLE_LEAVE'
        ) {
          return false;
        }
      }

      return true;
    });

    if (!filteredEvents?.length) {
      return false;
    }

    // From here, we should have only kept events that have the same fitter as the dossier, so we can take the fitter of the first event
    const eventsFitter = (
      filteredEvents[0]?.extendedProps as EventExtendedProps
    ).fitter;

    // The bounds of the appointment we are testing
    const dossierStartTime = dayjs(dossier.appointment?.time).tz(
      this.timezoneService.getFitterTimezone(eventsFitter)
    );
    const dossierEndTime = dayjs(dossier.appointment?.endTime).tz(
      this.timezoneService.getFitterTimezone(eventsFitter)
    );

    const dossierRVUsePeriod = getPeriodReplacementVehicleUse(dossier, {
      start: dossier.appointment?.time || dayjs().format(),
      end: dossier.appointment?.endTime || dayjs().format(),
    });

    // The bounds of the replacement vehicle use period
    const dossierRVUseStartTime = dayjs(dossierRVUsePeriod?.start).tz(
      this.timezoneService.getFitterTimezone(eventsFitter)
    );
    const dossierRVUseEndTime = dayjs(
      dossierRVUsePeriod?.end || dossierRVUsePeriod?.start
    ).tz(this.timezoneService.getFitterTimezone(eventsFitter));

    const usedDossierVehicle =
      options.vehicle || dossier?.appointment?.assignedReplacementVehicle;
    const dossierTechsToSearch = options.tech
      ? [options.tech.name]
      : dossier?.appointment?.assignedResources;
    const dossierInterventionVehicleToSearch = options.interventionVehicle
      ? options.interventionVehicle.name
      : dossier?.appointment?.assignedInterventionVehicle;

    return filteredEvents.some((event) => {
      const extendedProps = event?.extendedProps as EventExtendedProps;

      const isDossierEvent =
        this.resManagerService.isDossierEventExtendedProps(extendedProps);
      const isAgendaEvent =
        this.resManagerService.isGenericEventExtendedProps(extendedProps);

      // The bounds of the event shown in the calendar
      const eventStart = dayjs(
        this.planningCalendarService.computeTimeOffsetOffCalendar(
          event?.start,
          this.timezoneService.getFitterTimezone(eventsFitter)
        )
      );
      const eventEnd = dayjs(
        this.planningCalendarService.computeTimeOffsetOffCalendar(
          event?.end,
          this.timezoneService.getFitterTimezone(eventsFitter)
        )
      );

      // Check if the event is overlapping with the new bounds of the moved appointment
      const eventOverlapsAppointment =
        (!dossierEndTime.isValid() &&
          dossierStartTime.isSameOrAfter(eventStart) &&
          dossierStartTime.isBefore(eventEnd)) ||
        (dossierStartTime.isBefore(eventEnd) &&
          dossierEndTime.isAfter(eventStart));

      // Check if the event is overlapping with the bounds of replacement vehicle use period
      const eventOverlapsRVUsePeriod =
        dossierRVUseStartTime.isBefore(eventEnd) &&
        dossierRVUseEndTime.isAfter(eventStart);

      if (isDossierEvent && shouldCheckOverlaps) {
        const eventDossier = extendedProps?.dossier;

        if (eventOverlapsAppointment) {
          if (shouldCheckTechs) {
            const sameTechBooked =
              !!eventDossier?.appointment?.assignedResources?.some(
                (eventDossierTech) =>
                  dossierTechsToSearch?.includes(eventDossierTech)
              );

            // If the appointments periods overlap and both have the same technician, then block the update
            if (sameTechBooked) {
              return true;
            }
          }

          if (
            shouldCheckinterventionVehicles &&
            dossierInterventionVehicleToSearch
          ) {
            const sameinterventionVehicleBooked =
              eventDossier?.appointment?.assignedInterventionVehicle ===
              dossierInterventionVehicleToSearch;

            // If the appointments periods overlap and both have the same intervention Vehicle, then block the update
            if (sameinterventionVehicleBooked) {
              return true;
            }
          }
        }

        if (shouldCheckVehicles) {
          const eventDossierRVUsePeriod = getPeriodReplacementVehicleUse(
            eventDossier,
            {
              start: eventDossier.appointment?.time,
              end: eventDossier.appointment?.endTime,
            }
          );

          const eventDossierRVOverlap =
            dossierRVUseEndTime.isAfter(eventDossierRVUsePeriod.start) &&
            dossierRVUseStartTime.isBefore(eventDossierRVUsePeriod.end);

          const sameRVBooked =
            eventDossier?.appointment?.isReplacementVehicleSelected &&
            eventDossier?.appointment?.assignedReplacementVehicle &&
            usedDossierVehicle &&
            eventDossier?.appointment?.assignedReplacementVehicle?.name ===
              usedDossierVehicle?.name &&
            eventDossier.appointment.assignedReplacementVehicle.id ===
              usedDossierVehicle.id &&
            eventDossier?.appointment?.assignedReplacementVehicle
              ?.licensePlateNumber === usedDossierVehicle?.licensePlateNumber;

          // If the replacement vehicle use periods of the event and of the dossier are overlapping and they are the same vehicle, then update is blocked
          if (eventDossierRVOverlap && sameRVBooked) {
            return true;
          }
        }
      } else if (isAgendaEvent && shouldCheckLeaves) {
        const agendaEvent = extendedProps?.event;

        if (eventOverlapsAppointment) {
          if (shouldCheckTechs && agendaEvent?.type === 'TECH_LEAVE') {
            const sameTechInLeave = !!agendaEvent?.resources?.some(
              (agendaEventRes) =>
                dossierTechsToSearch?.includes(agendaEventRes.name)
            );

            // If the event is a tech leave, we test if it is overlapping with the moved appointment and if the appointment has the leaving tech
            if (sameTechInLeave) {
              return true;
            }
          }

          if (
            shouldCheckinterventionVehicles &&
            agendaEvent?.type === 'INTERVENTION_VEHICLE_LEAVE'
          ) {
            const sameInterventionVehicleInLeave =
              !!agendaEvent?.resources?.some((agendaEventRes) =>
                dossierInterventionVehicleToSearch?.includes(
                  agendaEventRes.name
                )
              );

            // If the event is an interventionVehicle leave, we test if it is overlapping with the moved appointment and if the appointment has the leaving interventionVehicle
            if (sameInterventionVehicleInLeave) {
              return true;
            }
          }
        }

        if (
          shouldCheckVehicles &&
          eventOverlapsRVUsePeriod &&
          agendaEvent?.type === 'RV_LEAVE'
        ) {
          // FIXME - change the following test after separating resources and replacement vehicles in the AgendaEvent model
          const sameRVInLeave =
            usedDossierVehicle &&
            !!agendaEvent?.resources?.some(
              (agendaEventRes) =>
                usedDossierVehicle.id + '##' + usedDossierVehicle.name ===
                agendaEventRes?.name
            );

          // If the event is a rv leave, we test if it is overlapping with the rv use period and if the appointment has the leaving rv
          if (sameRVInLeave) {
            return true;
          }
        }
      }
    });
  }

  isReplacementVehicleAlreadyUsed(dossier: Dossier, vehicle: FitterVehicle) {
    const agendaEvents = this.getAllEventsIntersectingDossier(dossier);

    const blockingEvent = this.eventsHaveBlockingAttributes(
      dossier,
      agendaEvents,
      {
        checks: ['vehicle', 'overlaps', 'leave_event'],
        vehicle: vehicle,
      }
    );

    return !!blockingEvent;
  }

  isTechnicianUnavailable(
    dossier: Dossier,
    technician: FitterResource,
    onlyLeave = false
  ) {
    const checkOptions: Parameters<
      PlanningSandboxService['eventsHaveBlockingAttributes']
    >[2]['checks'] = ['tech', 'leave_event'];
    if (!onlyLeave) {
      checkOptions.push('overlaps');
    }

    const agendaEvents = this.getAllEventsIntersectingDossier(dossier);

    const blockingEvent = this.eventsHaveBlockingAttributes(
      dossier,
      agendaEvents,
      {
        checks: checkOptions,
        tech: technician,
      }
    );

    return !!blockingEvent;
  }

  isInterventionVehicleUnavailable(
    dossier: Dossier,
    interventionVehicle: FitterResource,
    onlyLeave = false
  ) {
    const checkOptions: Parameters<
      PlanningSandboxService['eventsHaveBlockingAttributes']
    >[2]['checks'] = ['interventionVehicle', 'leave_event'];
    if (!onlyLeave) {
      checkOptions.push('overlaps');
    }

    const agendaEvents = this.getAllEventsIntersectingDossier(dossier);

    const blockingEvent = this.eventsHaveBlockingAttributes(
      dossier,
      agendaEvents,
      {
        checks: checkOptions,
        interventionVehicle: interventionVehicle,
      }
    );

    return !!blockingEvent;
  }

  isMinimumAddrElementsGiven(address: LocationDataGeocoded) {
    return (
      (!!address?.addressElements?.city &&
        !!address?.addressElements?.postalCode) ||
      (address.isCompleteAddress &&
        this.featureFlippingService.isFeatureActivated(
          'agenda-mobility-force-complete-address'
        ))
    );
  }

  getAgendaEventsSnapshot(
    noDuplicateDossier?: boolean
  ): (EventApi & { extendedProps: EventExtendedProps; resourceId: string })[] {
    let eventList = this.store?.selectSnapshot<
      (EventApi & { extendedProps: EventExtendedProps; resourceId: string })[]
    >((state) => state?.agendaEvents);

    if (noDuplicateDossier) {
      eventList = eventList?.filter((event) => {
        // Skip events of the replacement vehicle columns since they are only duplicates of other columns
        if (
          this.resManagerService.isAnyRVRes(
            this.resManagerService.getResourceType(event.resourceId)
          ) &&
          this.resManagerService.isDossierEventExtendedProps(
            event?.extendedProps
          )
        ) {
          return false;
        }

        return true;
      });
    }

    return eventList;
  }

  getSidexaData(
    licensePlateNumber: string,
    fitterId: string,
    glassType: Glass
  ) {
    return this.carHttpService
      .getSidexaData({ licensePlateNumber, fitterId, glassType })
      .pipe(take(1));
  }

  isAppointmentAlreadyExist(dossier: Dossier, dossiersValidated?: Dossier[]) {
    if (dossiersValidated) {
      const foundExistingDossiersValidated = dossiersValidated.some(
        (dossierValidated) => {
          return this.checkAppointmentAlreadyExist(dossier, dossierValidated);
        }
      );

      return !!foundExistingDossiersValidated;
    } else {
      const foundExistingEvent = this.getAllEventsIntersectingDossier(
        dossier
      )?.some((event) => {
        const extendedProps = event?.extendedProps as EventExtendedProps;

        if (
          !this.resManagerService.isDossierEventExtendedProps(extendedProps)
        ) {
          return false;
        }

        return this.checkAppointmentAlreadyExist(
          dossier,
          extendedProps?.dossier
        );
      });

      return !!foundExistingEvent;
    }
  }

  checkAppointmentAlreadyExist(dossier1: Dossier, dossier2: Dossier) {
    this.isDossierFinished;
    return (
      dossier1.id !== dossier2?.id &&
      this.isDossierActive(dossier1) &&
      this.isDossierActive(dossier2) &&
      dossier1?.claim?.car?.licensePlateNumber ===
        dossier2?.claim?.car?.licensePlateNumber &&
      dossier1?.appointment?.fitterId === dossier2?.appointment?.fitterId &&
      dossier1?.claim?.operationType === dossier2?.claim?.operationType &&
      dossier1?.claim?.glass?.glass === dossier2?.claim?.glass?.glass &&
      dayjs(dossier1?.appointment?.time).isSame(
        dossier2?.appointment?.time,
        'seconds'
      ) &&
      dayjs(dossier1?.appointment?.endTime).isSame(
        dossier2?.appointment?.endTime,
        'seconds'
      )
    );
  }

  canValidateDossier(dossier: Dossier) {
    return (
      !!dossier?.claim?.glass?.glass &&
      !!dossier?.appointment?.time &&
      !!dossier?.appointment?.endTime &&
      !!dossier?.appointment?.fitterId &&
      !!dossier?.claim?.car?.licensePlateNumber &&
      !!dossier?.claim?.operationType &&
      !!dossier?.customer?.lastname &&
      !!dossier?.customer?.phoneNumber?.number
    );
  }

  userIsSuperAdmin() {
    return this.authOktaService.userIsFitterSuperAdmin$();
  }

  userIsAdmin() {
    return this.authOktaService.userIsFitterAdmin$();
  }

  userHasFitterAdminGroupAccess(fitter: Fitter) {
    return this.authOktaService.userHasFitterAdminGroupAccess$(fitter);
  }

  getFittersSuggestions(
    queryStr: string
  ): Observable<{ id: string; name: string }[]> {
    return this.userIsSuperAdmin().pipe(
      take(1),
      switchMap((isSuperAdmin) => {
        if (!isSuperAdmin) {
          return of([]);
        }
        return this.agendaHttpService
          .getFitterSuggestions({ query: queryStr })
          .pipe(map((result) => result.fitters));
      })
    );
  }

  saveDraftDossier(dossier) {
    delete dossier?.creationData;
    delete dossier?.currentlyInUse;
    delete dossier?.previousAppointments;
    delete dossier?.createDate;
    delete dossier?.updateDate;
    delete dossier?.externalReferences;
    delete dossier?.stateHistory;
    delete dossier?.externalId;
    delete dossier?.declarationPage;
    delete dossier?.insurerData?.callAgentFirstname;
    delete dossier?.insurerData?.callAgentLastname;

    const dossierToUpdate = dossier;

    return this.storageHttpService
      .uploadDocuments(dossier?.claim?.documents ?? [])
      .pipe(
        tap((documents) => {
          dossierToUpdate.claim = {
            ...dossier.claim,
            documents: documents.map((doc) => {
              delete doc.file;
              return { ...doc };
            }),
          };
        }),
        switchMap(() => {
          return this.agendaHttpService.saveDraftDossier({
            dossier: dossierToUpdate,
          });
        })
      );
  }

  fetchDossiersByFitters(
    fitterId: string,
    begin: Date | string,
    end: Date | string
  ) {
    return this.agendaHttpService.getDossiers({
      fitterIds: [fitterId],
      status: ['NEW', 'PROGRAMMED', 'COMPLETED', 'CANCELLED', 'NO_SHOW'],
      filters: {
        startDate: begin,
        endDate: end,
      } as DossierAgendaFiltersInput,
    });
  }

  onLightUpdateDossier(dossier: Dossier) {
    return this.agendaHttpService.saveLightDossier({
      dossier: dossier as DossierInput,
    });
  }

  saveChatComments(idDossier: string, comments: DossierComments[]) {
    return this.dossierHttpService.saveChatComments({
      updateComments: { idDossier, comments },
    });
  }
}
